import React, { useCallback, useMemo, useState } from "react";
import { UserCPanelModel } from "@jugl-web/rest-api";
import { Subject } from "rxjs";
import { CPanelPageContext, CPanelPageContextValue } from "./CPanelPageContext";

export const CPanelPageProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<UserCPanelModel[]>([]);
  const [openedSideBar, setOpenedSideBar] =
    useState<CPanelPageContextValue["openedSideBar"]>(null);
  const [openedAlert, setOpenedAlert] =
    useState<CPanelPageContextValue["openedAlert"]>(null);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [isOutOfSeatsDialogOpen, setIsOutOfSeatsDialogOpen] = useState(false);
  const [userIdToDocCountDelta, setUserIdToDocCountDelta] = useState<
    Record<string, number>
  >({});

  const updateUserDocCountDelta = useCallback(
    (userId: string, delta: number) => {
      setUserIdToDocCountDelta((prev) => ({
        ...prev,
        [userId]: (prev[userId] || 0) + delta,
      }));
    },
    []
  );

  const resetUserDocCountDelta = useCallback(() => {
    setUserIdToDocCountDelta({});
  }, []);

  const toggleUserSelectState = useCallback(
    (selectedUser: UserCPanelModel) =>
      setSelectedUsers((prev) => {
        const isSelected = prev.some(
          (user) => user.entity_rel_id === selectedUser.entity_rel_id
        );
        return isSelected
          ? prev.filter(
              (user) => user.entity_rel_id !== selectedUser.entity_rel_id
            )
          : [...prev, selectedUser];
      }),
    []
  );

  const clearSelectedUsers = useCallback(() => setSelectedUsers([]), []);

  const refetchUsers$ = useMemo(() => new Subject<void>(), []);

  const updateUser$ = useMemo(
    () => new Subject<{ id: string; data: Partial<UserCPanelModel> }>(),
    []
  );

  const contextValue = useMemo<CPanelPageContextValue>(
    () => ({
      searchQuery,
      selectedUsers,
      setSearchQuery,
      toggleUserSelectState,
      clearSelectedUsers,
      openedSideBar,
      openedAlert,
      setOpenedSideBar,
      setOpenedAlert,
      userIdToDocCountDelta,
      updateUserDocCountDelta,
      resetUserDocCountDelta,
      refetchUsers$,
      updateUser$,
      totalUsersCount,
      setTotalUsersCount,
      isOutOfSeatsDialogOpen,
      setIsOutOfSeatsDialogOpen,
    }),
    [
      searchQuery,
      selectedUsers,
      setSearchQuery,
      toggleUserSelectState,
      clearSelectedUsers,
      openedSideBar,
      openedAlert,
      setOpenedSideBar,
      setOpenedAlert,
      userIdToDocCountDelta,
      updateUserDocCountDelta,
      resetUserDocCountDelta,
      refetchUsers$,
      updateUser$,
      totalUsersCount,
      isOutOfSeatsDialogOpen,
    ]
  );
  return (
    <CPanelPageContext.Provider value={contextValue}>
      {children}
    </CPanelPageContext.Provider>
  );
};
